import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { Loader2, Search, Tag, Edit, Trash2, Plus, Check, X, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { useDomainQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';

interface ShopCategory {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  created_at: string;
  tenant_id: number | null;
  domain?: string;
}

export const ShopCategoryList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const [categories, setCategories] = useState<ShopCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<ShopCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<ShopCategory | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategorySlug, setNewCategorySlug] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain } = getFilterParams();

  const fetchCategories = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('shop_categories')
        .select('*')
        .order('name');

      // Appliquer le filtre domaine uniquement si défini
      if (domain) {
        query = query.eq('domain', domain);
      }

      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,slug.ilike.%${searchQuery}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      setCategories(data || []);
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, [searchQuery]);

  const handleDeleteClick = (category: ShopCategory) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    setDeletingCategory(true);
    try {
      const { error } = await supabase
        .from('shop_categories')
        .delete()
        .eq('id', categoryToDelete.id);
      if (error) throw error;
      toast({ title: 'Catégorie supprimée', variant: 'success' });
      setDeleteDialogOpen(false);
      fetchCategories();
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setDeletingCategory(false);
    }
  };

  const handleEditClick = (category: ShopCategory) => {
    setCategoryToEdit(category);
    setEditName(category.name);
    setEditDescription(category.description);
    setEditSlug(category.slug);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!categoryToEdit) return;
    setSavingCategory(true);
    try {
      const { error } = await supabase
        .from('shop_categories')
        .update({
          name: editName,
          description: editDescription,
          slug: editSlug,
        })
        .eq('id', categoryToEdit.id);
      if (error) throw error;
      toast({ title: 'Catégorie modifiée', variant: 'success' });
      setEditDialogOpen(false);
      fetchCategories();
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setSavingCategory(false);
    }
  };

  const handleCreateCategory = async () => {
    setCreatingCategory(true);
    try {
      // Récupérer le domaine actuel
      const { domain } = getFilterParams();
      
      const { error } = await supabase
        .from('shop_categories')
        .insert({
          name: newCategoryName.trim(),
          slug: newCategorySlug.trim().toLowerCase(),
          description: newCategoryDescription.trim() || null,
          domain: domain // Utiliser le domaine au lieu de tenant_id
        });
      if (error) throw error;
      toast({ title: 'Catégorie créée', variant: 'success' });
      setCreateDialogOpen(false);
      setNewCategoryName('');
      setNewCategorySlug('');
      setNewCategoryDescription('');
      fetchCategories();
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setCreatingCategory(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Tag className="h-8 w-8 text-[#D4AF37]" />
              Gestion des Catégories
            </h1>
            <p className="text-gray-400 mt-2">
              {categories.length} catégorie{categories.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90">
            <Plus className="mr-2" size={18} />
            Nouvelle catégorie
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{categories.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Avec description</div>
            <div className="text-2xl font-bold text-blue-400">
              {categories.filter(c => c.description).length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Sans description</div>
            <div className="text-2xl font-bold text-gray-400">
              {categories.filter(c => !c.description).length}
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher par nom, slug ou description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
            />
          </div>
        </div>

        {/* Table des catégories */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucune catégorie trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Créée le
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {categories.map((category, index) => (
                    <tr
                      key={category.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                            <Tag className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{category.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{category.slug}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-300">{category.description || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{format(new Date(category.created_at), 'Pp', { locale: fr })}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditClick(category)} className="text-[#D4AF37] hover:text-[#B8941F]">
                            <Edit size={18} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(category)} className="text-red-400 hover:text-red-300">
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Dialogue création */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Nouvelle catégorie</DialogTitle>
            <DialogDescription className="text-gray-400">
              Ajouter une nouvelle catégorie de produit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label htmlFor="new-name" className="text-sm font-medium text-gray-300">
                Nom
              </label>
              <Input
                id="new-name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
              />
            </div>
            <div>
              <label htmlFor="new-slug" className="text-sm font-medium text-gray-300">
                Slug
              </label>
              <Input
                id="new-slug"
                value={newCategorySlug}
                onChange={(e) => setNewCategorySlug(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
              />
            </div>
            <div>
              <label htmlFor="new-description" className="text-sm font-medium text-gray-300">
                Description (optionnelle)
              </label>
              <Input
                id="new-description"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim() || !newCategorySlug.trim() || creatingCategory}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white hover:opacity-90"
            >
              {creatingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Créer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialogue édition */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Modifier la catégorie</DialogTitle>
            <DialogDescription className="text-gray-400">
              Modifier les informations de la catégorie.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label htmlFor="edit-name" className="text-sm font-medium text-gray-300">
                Nom
              </label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
              />
            </div>
            <div>
              <label htmlFor="edit-slug" className="text-sm font-medium text-gray-300">
                Slug
              </label>
              <Input
                id="edit-slug"
                value={editSlug}
                onChange={(e) => setEditSlug(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
              />
            </div>
            <div>
              <label htmlFor="edit-description" className="text-sm font-medium text-gray-300">
                Description (optionnelle)
              </label>
              <Input
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={!editName.trim() || !editSlug.trim() || savingCategory}
              className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
            >
              {savingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialogue suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Supprimer la catégorie</DialogTitle>
            <DialogDescription className="text-gray-400">
              Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          {categoryToDelete && (
            <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center">
                <Tag className="h-5 w-5 text-yellow-400 mr-2" />
                <span className="font-medium text-white">{categoryToDelete.name}</span>
              </div>
              {categoryToDelete.description && (
                <p className="mt-2 text-sm text-gray-400">{categoryToDelete.description}</p>
              )}
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingCategory}
              className="bg-gradient-to-r from-red-500 to-red-600 text-white hover:opacity-90"
            >
              {deletingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ShopCategoryList;
